import { Button, Container, createStyles, Grid, List, ListItem, makeStyles, Theme, Typography } from '@material-ui/core';
import React, { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { oper as v_oper, basic, Values } from '../request/values';
import { COLORS } from '../shared/Theme';
import { DialogInput, DialogDeleteCompany, DialogInputText, DialogEditCEODialog } from '../components/Dialog';
import ToastStr from '../request/toastStr';
import TitleBar from '../shared/header/TitleBar';
import { formatMoneyAmount } from '../locales/formatModule';

const SERVER = basic.server;

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		container: {
			padding: 0,
			height: '88vh',
			paddingTop: '3.5vh',
			background: `${COLORS.WHITE}`,
		},
		cardContainer: {
			width: '80%',
			margin: 'auto',
		},
		materialItem: {
			borderRadius: 12,
			margin: 'auto',
			marginBottom: '2vh',
			boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.16)',
			width: '90%',
			paddingTop: '2.2vh',
			paddingBottom: '2.2vh',
		},
		itemProcessing: {
			backgroundColor: `${COLORS.LIGHT_GREY}`,
		},

		list: {
			width: '100%',
			height: '75vh',
			overflowY: 'auto',
			fontFamily: 'NanumB',
			fontWeight: 'bold',
			fontSize: '4vw',
			paddingTop: '3vh',
		},
		grid: {
			maxHeight: '80vh',
			overflowY: 'auto',
		},
		paper: {
			padding: theme.spacing(1),
			// margin: 'auto'
		},
		materialImage: {
			// height: 60,
			// width: 60
			textAlign: 'center',
			margin: 'auto',
			'& img': {
				width: '11vw',
			},
		},
		materialList: {
			width: '90%',
			maxWidth: 360,
		},
		materialCompanyName: {
			fontSize: '3vw',
			fontWeight: 'bold',
			fontFamily: 'NanumB',
			color: `${COLORS.WATER_BLUE}`,
		},
		orderSheet: {
			// height : 'calc(100%- 64px)',
			// top: 64
			height: '100vh',
		},
		empty: {
			height: '62vh',
			textAlign: 'center',
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			'& img': {
				width: '35vw',
				height: '35vw',
				marginBottom: '2.5vh',
				opacity: 0.4,
			},
			'& h1': {
				color: `${COLORS.DOWN}`,
			},
		},

		xIconContainer: {
			position: 'absolute',
			top: '-8px',
			right: '-8px',
			[theme.breakpoints.up('md')]: {
				top: '-14px',
				right: '-14px',
			},
			'& #xIconButton': {
				display: 'box',
				all: 'unset',
				border: 'none',
				background: 'none',
				'& #xIcon': {
					display: 'box',
					width: '20px',
					[theme.breakpoints.up('md')]: {
						width: '32px',
					},
				},
			},
		},
		infoContainer: {
			width: '100%',
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'center',
			alignItems: 'center',
			gap: '10px',
			'& #companyList': {
				width: '100%',
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'center',
				alignItems: 'flex-start',
				'& #companyName': {
					display: 'flex',
					alignItems: 'center',
					textAlign: 'left',
					padding: '5px',
					'& #editButton': {
						display: 'box',
						all: 'unset',
						border: 'none',
						background: 'none',
						marginLeft: '1.5vw',
						'& #editIcon': {
							display: 'box',
							width: '3vw',
							maxWidth: '24px',
						},
					},
				},
				'& #companyAsset': {
					display: 'flex',
					alignItems: 'center',
					padding: '5px',
				},
			},
			'& #buttonContainer': {
				width: '100%',
				display: 'flex',
				flexDirection: 'row',
				justifyContent: 'center',
				gap: '20px',
				'& #loanButton': {
					borderRadius: '5px',
					backgroundColor: `${COLORS.WATER_BLUE}`,
					width: '50%',
					padding: '10px',
					'& #loanButtonText': {
						color: `${COLORS.WHITE}`,
						fontFamily: 'NanumB',
						fontWeight: 'bold',
						fontSize: '12px',
						[theme.breakpoints.up('md')]: {
							fontSize: '18px',
						},
					},
				},
			},
		},
	})
);

type iCompanyList = {
	company_num: string;
	company_name: string;
	company_stock: string;
	member_name: string;
};

export interface InputProps {
	oper: any;
	setMember: (member: any) => void;
}

const OperCompanyList: React.FC<InputProps> = (props) => {
	const classes = useStyles();
	const { t } = useTranslation('oper');
	const [company, setCompany] = useState<iCompanyList[]>([]);
	const isMountedRef = React.useRef(true);

	const history = useHistory();

	const { oper, setMember } = props;

	const getCompanyList = () => {
		if (!oper?.id) return;
		axios({
			method: 'POST',
			data: {
				userId: oper.id,
			},
			withCredentials: true,
			url: SERVER + v_oper.oper + v_oper.company_list,
		})
			.then((res) => {
				return res.data;
			})
			.then((data: any) => {
				if (!isMountedRef.current) return;
				if (data.code == Values.SUCCESS_CODE) {
					setCompany(data.data ?? []);
					const classCode = data.class_code ?? '';
					if (classCode && classCode !== (oper?.classCode ?? '')) {
						setMember({ ...oper, classCode });
					}
				}
			})
			.catch(() => {
				// 언마운트/네트워크 오류 시 무시
			});
	};

	useEffect(() => {
		isMountedRef.current = true;
		getCompanyList();
		return () => {
			isMountedRef.current = false;
		};
	}, [oper?.id]);

	return (
		<Container component="main" className={classes.container}>
			<TitleBar history={history} title={t('OperCompanyList.pageTitle')} titleIcon="order" />

			<div className={classes.cardContainer} data-testid="oper-company-list-dashboard">
				<List className={classes.list}>
					{company && company.length > 0 ? (
						company.map((item: any, index: number) => {
							return (
								<ListItem key={item.company_num ?? item.company_name ?? index} className={classes.materialItem} alignItems="center">
									<CompanyItem item={item} index={index} getCompanyList={getCompanyList} />
								</ListItem>
							);
						})
					) : (
						<div className={classes.empty}>
							<div>
								<img src="/images/title/order.webp" />
								<Typography variant="h1">{t('OperCompanyList.emptyTitle')}</Typography>
							</div>
						</div>
					)}
				</List>
			</div>
		</Container>
	);
};

const CompanyItem: React.FC<any> = (props) => {
	const classes = useStyles();
	const history = useHistory();

	const { item, index, getCompanyList } = props;
	const { enqueueSnackbar } = useSnackbar();
	const [upInput, setUpInput] = useState<boolean>(false);
	const [downInput, setDownInput] = useState<boolean>(false);
	const [deleteDialog, setDeleteDialog] = useState<boolean>(false);
	const [companyInfo, setCompanyInfo] = useState();
	const [editDialog, setEditDialog] = useState<boolean>(false);
	const [editCEODialog, setEditCEODialog] = useState<boolean>(false);
	const { t } = useTranslation('oper');

	const handleDialogClose = (key: string) => {
		if (key === 'upInput') setUpInput((prevState) => !prevState);
		else if (key === 'downInput') setDownInput((prevState) => !prevState);
		else if (key === 'deleteDialog') setDeleteDialog((prevState) => !prevState);
		else if (key === 'editDialog') setEditDialog((prevState) => !prevState);
		else if (key === 'editCEODialog') setEditCEODialog((prevState) => !prevState);
	};

	const handleDialogOpen = (key: string) => {
		if (key === 'upInput') setUpInput(true);
		else if (key === 'downInput') setDownInput(true);
		else if (key === 'editDialog') setEditDialog(true);
	};

	const handleDeleteDialogOpen = async () => {
		await getCompanyInfo();
		setDeleteDialog(true);
	};

	const handleEditCEODialogOpen = async () => {
		await getCompanyInfo();
		setEditCEODialog(true);
	};

	const raiseMoney = (money: number) => {
		axios({
			method: 'POST',
			withCredentials: true,
			data: {
				companyNum: item.company_num,
				money: money,
			},
			url: SERVER + v_oper.oper + v_oper.company_raise,
		})
			.then((res: any) => {
				if (res.data.code === Values.SUCCESS_CODE) {
					enqueueSnackbar(ToastStr.COMPANY_ASSET_UPDATE_STR, {
						variant: 'success',
					});
				}
				console.log('res? ', res);
				getCompanyList();
			})
			.catch(() => {
				enqueueSnackbar(ToastStr.REQUEST_GET_POST_FAIL_STR, {
					variant: 'warning',
				});
			});
		handleDialogClose('upInput');
	};

	const disraiseMoney = (money: number) => {
		axios({
			method: 'POST',
			withCredentials: true,
			data: {
				companyNum: item.company_num,
				money: money,
			},
			url: SERVER + v_oper.oper + v_oper.company_lower,
		})
			.then((res: any) => {
				if (res.data.code === Values.SUCCESS_CODE) {
					enqueueSnackbar(ToastStr.COMPANY_ASSET_UPDATE_STR, {
						variant: 'success',
					});
				}
				console.log('res? ', res);
				getCompanyList();
			})
			.catch(() => {
				enqueueSnackbar(ToastStr.REQUEST_GET_POST_FAIL_STR, {
					variant: 'warning',
				});
			});
		handleDialogClose('downInput');
	};

	const getCompanyInfo = async () => {
		try {
			const response = await axios({
				method: 'GET',
				withCredentials: true,
				params: {
					companyNum: item.company_num,
				},
				url: SERVER + v_oper.oper + v_oper.company_info,
			});
			console.log(response.data);
			setCompanyInfo(response.data.students);
		} catch (error) {
			console.error('Error fetching company info:', error);
		}
	};

	const deleteCompany = () => {
		axios({
			method: 'POST',
			withCredentials: true,
			data: {
				companyNum: item.company_num,
			},
			url: SERVER + v_oper.oper + v_oper.company_delete,
		})
			.then((res) => {
				console.log('data? ', res.data);
				enqueueSnackbar(ToastStr.COMPANY_DELETE_SUCCESS_STR, {
					variant: 'success',
				});
				//handleDialogClose('deleteDialog');
				getCompanyList();
			})
			.catch(() => {
				enqueueSnackbar(ToastStr.COMPANY_DELETE_FAIL_STR, {
					variant: 'warning',
				});
			});
	};

	const editCompanyCEO = (email: string) => {
		if (!email) {
			enqueueSnackbar(t('OperCompanyList.editCeoTitle'), {
				variant: 'warning',
			});
			return;
		}
		axios({
			method: 'POST',
			withCredentials: true,
			data: {
				companyNum: item.company_num,
				ceoEmail: email,
			},
			url: SERVER + v_oper.oper + v_oper.company_ceo_update,
		})
			.then((res: any) => {
				console.log('post edit? ', res.data);
				getCompanyList();
			})
			.catch(() => {
				enqueueSnackbar(ToastStr.REQUEST_GET_POST_FAIL_STR, {
					variant: 'warning',
				});
			});
		handleDialogClose('editCEODialog');
	};

	const editCompanyName = (name: string) => {
		axios({
			method: 'POST',
			withCredentials: true,
			data: {
				companyNum: item.company_num,
				name: name,
			},
			url: SERVER + v_oper.oper + v_oper.company_info_update,
		})
			.then((res: any) => {
				console.log('post edit? ', res.data);
				getCompanyList();
			})
			.catch(() => {
				enqueueSnackbar(ToastStr.REQUEST_GET_POST_FAIL_STR, {
					variant: 'warning',
				});
			});
		handleDialogClose('editDialog');
	};

	return (
		<Grid container alignContent="center" data-testid={`oper-company-${item.company_name}`}>
			<Grid className={classes.xIconContainer}>
				<button id="xIconButton" onClick={handleDeleteDialogOpen} data-testid={`oper-company-delete-btn-${item.company_name}`}>
					<img src="/images/x_icon.png" id="xIcon" />
				</button>
			</Grid>
			<div className={classes.infoContainer}>
				<Grid id="companyList">
					<Grid id="companyName">
						<Typography
							display="inline"
							variant="h3"
							style={{
								color: `${COLORS.BROWNISH_GREY}`,
								fontWeight: 'bold',
							}}
							data-testid={`oper-company-name-label-${item.company_name}`}
						>
							{t('OperCompanyList.companyNameLabel')} {`${item.company_name}`}
						</Typography>
						<button id="editButton" onClick={() => handleDialogOpen('editDialog')} data-testid={`oper-company-name-edit-btn-${item.company_name}`}>
							<img id="editIcon" src="/images/edit_icon.svg" />
						</button>
					</Grid>
					<Grid id="companyName">
						<Typography
							display="inline"
							variant="h3"
							style={{
								color: `${COLORS.BROWNISH_GREY}`,
								fontWeight: 'bold',
							}}
							data-testid={`oper-company-ceo-label-${item.member_name}`}
						>
							{t('OperCompanyList.ceoLabel')} {item.member_name ?? ''}
						</Typography>
						<button id="editButton" onClick={() => handleEditCEODialogOpen()} data-testid={`oper-company-ceo-edit-btn-${item.company_name}`}>
							<img id="editIcon" src="/images/edit_icon.svg" />
						</button>
					</Grid>

					<Grid id="companyAsset">
						<Typography
							display="inline"
							variant="h3"
							style={{
								color: `${COLORS.WATER_BLUE}`,
								fontWeight: 'bold',
							}}
							data-testid={`oper-${item.company_name}-asset-stock`}
						>
							{t('OperCompanyList.assetLabel')} {`${formatMoneyAmount(item.company_stock)}`}
						</Typography>
					</Grid>
				</Grid>
				<div id="buttonContainer">
					<Button id="loanButton" onClick={() => handleDialogOpen('upInput')} data-testid={`oper-${item.company_name}-asset-raise-btn`}>
						<Typography variant="h3" id="loanButtonText">
							{t('OperCompanyList.raiseAsset')}
						</Typography>
					</Button>
					<Button id="loanButton" onClick={() => handleDialogOpen('downInput')} data-testid={`oper-${item.company_name}-asset-lower-btn`}>
						<Typography variant="h3" id="loanButtonText">
							{t('OperCompanyList.lowerAsset')}
						</Typography>
					</Button>

					<DialogInput
						open={upInput}
						handleClose={() => handleDialogClose('upInput')}
						title={t('OperCompanyList.inputRaiseAssetTitle')}
						yesConfirm={(e: any, money: number) => raiseMoney(money)}
						noConfirm={() => handleDialogClose('upInput')}
					/>

					<DialogInput
						open={downInput}
						handleClose={() => handleDialogClose('downInput')}
						title={t('OperCompanyList.inputLowerAssetTitle')}
						yesConfirm={(e: any, money: number) => disraiseMoney(money)}
						noConfirm={() => handleDialogClose('downInput')}
					/>

					<DialogInputText
						open={editDialog}
						handleClose={() => handleDialogClose('editDialog')}
						title={t('OperCompanyList.editCompanyNameTitle')}
						yesConfirm={(e: any, name: string) => editCompanyName(name)}
						noConfirm={() => handleDialogClose('editDialog')}
					/>

					<DialogEditCEODialog
						open={editCEODialog}
						memberList={companyInfo}
						title={t('OperCompanyList.editCeoTitle')}
						emptyText={t('OperCompanyList.editCeoNoMembers')}
						handleClose={() => handleDialogClose('editCEODialog')}
						yesConfirm={(email: string) => editCompanyCEO(email)}
						noConfirm={() => handleDialogClose('editCEODialog')}
					/>

					<DialogDeleteCompany
						open={deleteDialog}
						handleClose={() => handleDialogClose('deleteDialog')}
						yesConfirm={() => deleteCompany()}
						noConfirm={() => handleDialogClose('deleteDialog')}
						title={t('OperCompanyList.deleteCompanyTitle')}
						context={companyInfo}
					/>
				</div>
			</div>
		</Grid>
	);
};

export default OperCompanyList;
